/**
 * Odznaky (počty) u položek menu, jeden společný zdroj pro postranní panel
 * (layout/Sidebar.jsx) i spodní lištu na telefonu (layout/SpodniLista.jsx).
 *
 * 1. 11. 2026: dřív si počty načítal jen Sidebar a spodní lišta měla vlastní
 * dotaz jen na absence, takže na telefonu chyběly ostatní odznaky (Hodnocení
 * výživy, Volání, Bonusy a srážky, Upomínky). Teď se počty načtou jednou,
 * uloží sem a obě komponenty z nich jen čtou. Žádný dotaz se nedělá dvakrát.
 *
 * Klíč = badgeKey z lib/menu.js, hodnota = počet (0 = nezobrazit).
 */
import { useEffect, useState } from "react";
import { getCurrentUser } from "./auth.js";

let stav = {};
const posluchaci = new Set();

function nastav(castecne) {
  stav = { ...stav, ...castecne };
  posluchaci.forEach((f) => f(stav));
}

// Načítání běží jednou pro aktuální roli, bez ohledu na to, kolik komponent odznaky čte.
let aktivniRole = null;
let pocetOdberu = 0;
let uklid = null;

function spust(role) {
  let zij = true;
  const uklidy = [];

  // HR počty (upomínky na limitu, bonusy a srážky, absence), jen owner a manažer.
  if (role === "owner" || role === "manager") {
    const loadBadges = async () => {
      try {
        const [rU, rB, rA] = await Promise.all([
          fetch("/api/hr-upominky-summary", { credentials: "include" }),
          fetch("/api/hr-bs-summary", { credentials: "include" }),
          fetch("/api/hr-absence?count=pending", { credentials: "include" }),
        ]);
        const next = {};
        if (rU.ok) {
          const j = await rU.json();
          next.upominky_at_limit = j.atLimit || 0;
        }
        if (rB.ok) {
          const j = await rB.json();
          next.bs_caka = j.badgeCount || 0;
        }
        if (rA.ok) {
          const j = await rA.json();
          // 21.9.2026: absence schvaluje jen owner, číslo je jen pro něj.
          next.absence_pending = role === "owner" ? (j.count || 0) : 0;
        }
        if (zij) nastav(next);
      } catch { /* swallow, chyba odznaku neshazuje aplikaci */ }
    };
    loadBadges();
    const interval = setInterval(loadBadges, 15 * 60 * 1000); // refresh každých 15 min (25.5.2026: 5→15 min, Netlify DB credit optimalizace)
    uklidy.push(() => clearInterval(interval));
  }

  // 1. 10. 2026: počet lidí k zavolání u položky Volání (sales). Číslo posílá
  // stránka Volání po každém načtení fronty, žádný další dotaz na server.
  const naSales = (e) => nastav({ sales_dnes: Number(e.detail) || 0 });
  window.addEventListener("fw40-sales-pocet", naSales);
  uklidy.push(() => window.removeEventListener("fw40-sales-pocet", naSales));

  // 1. 11. 2026: odznak u Hodnocení výživy (kolika klientkám po nutriční konzultaci
  // zbývá zavolat). Jeden dotaz při otevření, pak číslo posílá stránka po každém uložení.
  const r = String(role || "").toLowerCase();
  if (["sales", "csm", "owner"].includes(r)) {
    const naVyzivu = (e) => nastav({ hodnoceni_vyzivy: Number(e.detail) || 0 });
    window.addEventListener("fw40-hodnoceni-vyzivy-pocet", naVyzivu);
    uklidy.push(() => window.removeEventListener("fw40-hodnoceni-vyzivy-pocet", naVyzivu));
    fetch("/api/hodnoceni-vyzivy?akce=pocet", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((j) => { if (zij && j) nastav({ hodnoceni_vyzivy: Number(j.pocet) || 0 }); })
      .catch(() => { /* odznak není nutný */ });
  }

  return () => { zij = false; uklidy.forEach((f) => f()); };
}

function pripoj(role) {
  if (!role) return () => {};
  if (aktivniRole === role) {
    pocetOdberu += 1;
  } else {
    if (uklid) uklid();
    // Při změně role (náhled jako) se staré počty zahodí, ať nesvítí cizí čísla.
    stav = {};
    posluchaci.forEach((f) => f(stav));
    aktivniRole = role;
    pocetOdberu = 1;
    uklid = spust(role);
  }
  return () => {
    if (aktivniRole !== role) return;
    pocetOdberu -= 1;
    if (pocetOdberu <= 0) {
      if (uklid) uklid();
      uklid = null;
      aktivniRole = null;
      pocetOdberu = 0;
    }
  };
}

/** Vrací objekt { [badgeKey]: počet } sdílený napříč komponentami. */
export function useMenuOdznaky() {
  const role = getCurrentUser()?.role || "";
  const [odznaky, setOdznaky] = useState(stav);
  useEffect(() => {
    posluchaci.add(setOdznaky);
    setOdznaky(stav);
    return () => { posluchaci.delete(setOdznaky); };
  }, []);
  useEffect(() => pripoj(role), [role]);
  return odznaky;
}

/** Popisek odznaku do title (najetí myší, čtečka). */
export function popisOdznaku(badgeKey, n) {
  if (badgeKey === "absence_pending") return `${n} absencí čeká na schválení`;
  if (badgeKey === "upominky_at_limit") return `${n} osob s 3+ upomínkami tento měsíc`;
  if (badgeKey === "sales_dnes") return `${n} lidí dnes čeká na zavolání`;
  if (badgeKey === "hodnoceni_vyzivy") return `${n} klientek po nutriční konzultaci čeká na zavolání`;
  if (badgeKey === "bs_caka") return `${n} bonusů a srážek čeká`;
  return String(n);
}
